import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Shared pill-style enable/disable switch — used anywhere the app needs a
 * boolean toggle (SMTP/SMS enable, notification prefs, etc.) so every form
 * uses the same control instead of some pages using a native checkbox and
 * others a custom switch.
 */
const Toggle = React.forwardRef(({ checked, onChange, label, className, disabled, ...props }, ref) => {
  const track = (
    <div
      className={cn(
        "w-11 h-6 rounded-full relative transition-colors duration-200 shrink-0",
        checked ? "bg-[#0176D3]" : "bg-[#D8DDE6]",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className={cn(
        "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200",
        checked ? "translate-x-6" : "translate-x-1"
      )} />
    </div>
  );

  if (!label) {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        className={cn("inline-flex", className)}
        {...props}
      >
        {track}
      </button>
    );
  }

  return (
    <label className={cn("flex items-center gap-4 cursor-pointer", disabled && "cursor-not-allowed", className)}>
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        {...props}
      >
        {track}
      </button>
      <span className="text-[12px] font-bold text-[#5C5C5C] uppercase tracking-wider">{label}</span>
    </label>
  );
});

Toggle.displayName = 'Toggle';

export default Toggle;
export { Toggle };
